import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Inbox,
  Loader2,
  Mail,
  Phone,
  Trash2,
  Users2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useEnquiryList } from "@/modules/enquiries/hooks/useEnquiries";
import {
  useAssignLeadOwner,
  useDeleteLead,
  useLead,
  useLeadStatuses,
  useLeadTimeline,
  useUpdateLead,
} from "../hooks/useLeads";
import { LeadStatusBadge } from "../components/LeadStatusBadge";
import { FindDuplicatesModal } from "../components/FindDuplicatesModal";

const CARD_BORDER = "hsl(210,30%,85%)";

type TabKey = "enquiries" | "notes" | "tasks" | "activity";

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="text-sm text-foreground">{value || "—"}</div>
    </div>
  );
}

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString("en-GB") : "—";
}

export default function LeadDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const perms = user?.permissions?.leads;
  const canEdit = !!perms?.canEdit;
  const canDelete = !!perms?.canDelete;
  const canMerge = !!perms?.canAdmin;

  const { data: lead, isLoading, isError } = useLead(id);
  const { data: statuses = [] } = useLeadStatuses();
  const users = useUserList({ limit: 100, status: "ACTIVE" });

  const update = useUpdateLead(id);
  const assign = useAssignLeadOwner(id);
  const del = useDeleteLead();

  const [tab, setTab] = useState<TabKey>("enquiries");
  const [editingPerson, setEditingPerson] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [editingTags, setEditingTags] = useState(false);
  const [findDupOpen, setFindDupOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Person-info edit buffer
  const [pName, setPName] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pPhone, setPPhone] = useState("");
  const [pCompany, setPCompany] = useState("");
  const [pJob, setPJob] = useState("");
  const [pCountry, setPCountry] = useState("");

  // Notes / tags edit buffers
  const [notesDraft, setNotesDraft] = useState("");
  const [tagsDraft, setTagsDraft] = useState("");

  // Enquiries tab — filter by this Lead
  const enquiries = useEnquiryList({ leadId: id, limit: 50 });
  // Activity tab — timeline
  const timeline = useLeadTimeline(id);

  const breadcrumb = useMemo(
    () => [
      { label: "PhazeX Hub", href: "/" },
      { label: "CRM" },
      { label: "Leads", href: "/leads" },
      { label: lead?.code ?? "…" },
    ],
    [lead?.code],
  );

  if (isLoading) {
    return (
      <AppLayout breadcrumb={breadcrumb}>
        <div className="flex items-center justify-center py-20 text-sm text-muted-foreground">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
          Loading lead…
        </div>
      </AppLayout>
    );
  }
  if (isError || !lead) {
    return (
      <AppLayout breadcrumb={breadcrumb}>
        <div className="py-20 text-center text-sm text-muted-foreground">
          Lead not found.
        </div>
      </AppLayout>
    );
  }

  function startPersonEdit() {
    setPName(lead!.name ?? "");
    setPEmail(lead!.email ?? "");
    setPPhone(lead!.phone ?? "");
    setPCompany(lead!.companyName ?? "");
    setPJob(lead!.jobTitle ?? "");
    setPCountry(lead!.country ?? "");
    setEditingPerson(true);
  }

  async function savePerson() {
    await update.mutateAsync({
      name: pName.trim() || null,
      email: pEmail.trim() || null,
      phone: pPhone.trim() || null,
      companyName: pCompany.trim() || null,
      jobTitle: pJob.trim() || null,
      country: pCountry.trim() || null,
    });
    setEditingPerson(false);
  }

  function startNotesEdit() {
    setNotesDraft(lead!.notes ?? "");
    setEditingNotes(true);
  }

  async function saveNotes() {
    await update.mutateAsync({ notes: notesDraft.trim() || null });
    setEditingNotes(false);
  }

  function startTagsEdit() {
    setTagsDraft(lead!.tags.join(", "));
    setEditingTags(true);
  }

  async function saveTags() {
    const tags = tagsDraft
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    await update.mutateAsync({ tags });
    setEditingTags(false);
  }

  const mergedAway = !!lead.mergedIntoId;

  return (
    <AppLayout breadcrumb={breadcrumb}>
      <div className="w-full space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate("/leads")}
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            Back to Leads
          </button>
          <div className="flex items-center gap-2">
            {canMerge && !mergedAway && (
              <Button variant="outline" onClick={() => setFindDupOpen(true)}>
                <Users2 className="h-4 w-4" strokeWidth={1.5} />
                Find Duplicates
              </Button>
            )}
            {canDelete && !mergedAway && (
              <Button variant="outline" onClick={() => setDeleteOpen(true)}>
                <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                Delete
              </Button>
            )}
          </div>
        </div>

        {mergedAway && (
          <div className="flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            <div>
              This Lead was merged into{" "}
              <button
                className="font-mono underline"
                onClick={() => navigate(`/leads/${lead.mergedIntoId}`)}
              >
                {lead.mergedIntoCode ?? lead.mergedIntoId}
              </button>{" "}
              on {formatDate(lead.mergedAt)}. It is kept for audit history;
              edits are disabled.
            </div>
          </div>
        )}

        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="p-6">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold text-foreground">
                {lead.name ?? "(unnamed lead)"}
              </h1>
              <ReferenceIdBadge id={lead.code} />
              <LeadStatusBadge status={lead.status} />
              {lead.ownerName && (
                <span className="text-xs text-muted-foreground">
                  Owner: <span className="text-foreground">{lead.ownerName}</span>
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                · {lead.enquiryCount} enquir{lead.enquiryCount === 1 ? "y" : "ies"}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Person Info */}
        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="space-y-5 p-6">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Person Info</h3>
              {canEdit && !mergedAway && !editingPerson && (
                <Button variant="outline" onClick={startPersonEdit}>
                  Edit
                </Button>
              )}
              {editingPerson && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setEditingPerson(false)}
                    disabled={update.isPending}
                  >
                    Cancel
                  </Button>
                  <Button onClick={savePerson} disabled={update.isPending}>
                    {update.isPending && (
                      <Loader2
                        className="mr-1 h-4 w-4 animate-spin"
                        strokeWidth={1.5}
                      />
                    )}
                    Save
                  </Button>
                </div>
              )}
            </div>
            {!editingPerson ? (
              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                <Field label="Name" value={lead.name} />
                <Field
                  label="Email"
                  value={
                    lead.email ? (
                      <a
                        href={`mailto:${lead.email}`}
                        className="inline-flex items-center gap-1.5 text-primary hover:underline"
                      >
                        <Mail className="h-3.5 w-3.5" /> {lead.email}
                      </a>
                    ) : null
                  }
                />
                <Field
                  label="Phone"
                  value={
                    lead.phone ? (
                      <a
                        href={`tel:${lead.phone}`}
                        className="inline-flex items-center gap-1.5 text-primary hover:underline"
                      >
                        <Phone className="h-3.5 w-3.5" /> {lead.phone}
                      </a>
                    ) : null
                  }
                />
                <Field label="Company" value={lead.companyName} />
                <Field label="Job Title" value={lead.jobTitle} />
                <Field label="Country" value={lead.country} />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Name</Label>
                  <Input value={pName} onChange={(e) => setPName(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={pEmail}
                    onChange={(e) => setPEmail(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Phone</Label>
                  <Input
                    value={pPhone}
                    onChange={(e) => setPPhone(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Company</Label>
                  <Input
                    value={pCompany}
                    onChange={(e) => setPCompany(e.target.value)}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label>Job Title</Label>
                  <Input value={pJob} onChange={(e) => setPJob(e.target.value)} />
                </div>
                <div className="space-y-1.5">
                  <Label>Country</Label>
                  <Input
                    value={pCountry}
                    onChange={(e) => setPCountry(e.target.value)}
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pipeline */}
        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="space-y-5 p-6">
            <h3 className="text-sm font-semibold text-foreground">Pipeline</h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              <div className="space-y-1.5">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Status
                </div>
                {canEdit && !mergedAway ? (
                  <Select
                    value={lead.status}
                    onChange={(e) => update.mutate({ status: e.target.value })}
                    className="w-60"
                  >
                    {statuses.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <LeadStatusBadge status={lead.status} />
                )}
              </div>
              <div className="space-y-1.5">
                <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  Owner
                </div>
                {canEdit && !mergedAway ? (
                  <Select
                    value={lead.ownerId ?? ""}
                    onChange={(e) => assign.mutate(e.target.value || null)}
                    className="w-60"
                  >
                    <option value="">— Unassigned —</option>
                    {users.data?.data.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <div className="text-sm">{lead.ownerName ?? "—"}</div>
                )}
              </div>
              <Field
                label="First Enquiry"
                value={formatDate(lead.firstEnquiryAt)}
              />
              <Field label="Last Enquiry" value={formatDate(lead.lastEnquiryAt)} />
              <Field label="Enquiries" value={String(lead.enquiryCount)} />
              <Field label="Created" value={formatDate(lead.createdAt)} />

              <div className="col-span-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Tags
                  </div>
                  {canEdit && !mergedAway && !editingTags && (
                    <button
                      onClick={startTagsEdit}
                      className="text-xs text-primary hover:underline"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {!editingTags ? (
                  <div className="flex flex-wrap gap-1.5">
                    {lead.tags.length === 0 ? (
                      <span className="text-sm text-muted-foreground">—</span>
                    ) : (
                      lead.tags.map((t) => (
                        <span
                          key={t}
                          className="rounded-full bg-[hsl(210,30%,94%)] px-2 py-0.5 text-xs text-[hsl(215,25%,35%)]"
                        >
                          {t}
                        </span>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={tagsDraft}
                      onChange={(e) => setTagsDraft(e.target.value)}
                      placeholder="comma, separated, tags"
                    />
                    <Button onClick={saveTags} disabled={update.isPending}>
                      Save
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setEditingTags(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}
              </div>

              <div className="col-span-2 space-y-1.5">
                <div className="flex items-center justify-between">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
                    Notes
                  </div>
                  {canEdit && !mergedAway && !editingNotes && (
                    <button
                      onClick={startNotesEdit}
                      className="text-xs text-primary hover:underline"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {!editingNotes ? (
                  <div className="whitespace-pre-wrap text-sm text-foreground">
                    {lead.notes || "—"}
                  </div>
                ) : (
                  <div className="space-y-2">
                    <textarea
                      value={notesDraft}
                      onChange={(e) => setNotesDraft(e.target.value)}
                      rows={5}
                      className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                      style={{ borderColor: "hsl(210,30%,85%)" }}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setEditingNotes(false)}
                      >
                        Cancel
                      </Button>
                      <Button onClick={saveNotes} disabled={update.isPending}>
                        Save
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Linked Entities */}
        <Card style={{ borderColor: CARD_BORDER }}>
          <CardContent className="space-y-5 p-6">
            <h3 className="text-sm font-semibold text-foreground">
              Linked Entities
            </h3>
            <div className="grid grid-cols-3 gap-x-8 gap-y-4">
              <Field
                label="Contact"
                value={
                  lead.linkedContactId ? (
                    <button
                      onClick={() => navigate(`/contacts/${lead.linkedContactId}`)}
                      className="text-primary hover:underline"
                    >
                      {lead.linkedContactName ?? "View contact"} →
                    </button>
                  ) : (
                    <span className="text-xs text-muted-foreground">
                      Not yet — convert one of this Lead's enquiries to create
                      and link a Contact.
                    </span>
                  )
                }
              />
              <Field
                label="Company"
                value={
                  lead.linkedCompanyId ? (
                    <button
                      onClick={() => navigate(`/companies/${lead.linkedCompanyId}`)}
                      className="text-primary hover:underline"
                    >
                      {lead.linkedCompanyName ?? "View company"} →
                    </button>
                  ) : null
                }
              />
              <Field
                label="Project"
                value={
                  lead.linkedProjectId ? (
                    <button
                      onClick={() => navigate(`/projects/${lead.linkedProjectId}`)}
                      className="text-primary hover:underline"
                    >
                      {lead.linkedProjectName ?? "View project"} →
                    </button>
                  ) : null
                }
              />
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <div className="border-b border-border">
          <nav className="-mb-px flex gap-4">
            {([
              { k: "enquiries", l: `Enquiries (${lead.enquiryCount})` },
              { k: "notes", l: "Notes" },
              { k: "tasks", l: "Tasks" },
              { k: "activity", l: "Activity" },
            ] as { k: TabKey; l: string }[]).map((t) => (
              <button
                key={t.k}
                onClick={() => setTab(t.k)}
                className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium transition-colors ${
                  tab === t.k
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {t.l}
              </button>
            ))}
          </nav>
        </div>

        {tab === "enquiries" && (
          <Card style={{ borderColor: CARD_BORDER }}>
            <CardContent className="p-0">
              {enquiries.isLoading ? (
                <div className="flex items-center justify-center py-10 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                  Loading enquiries…
                </div>
              ) : (enquiries.data?.data ?? []).length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-sm text-muted-foreground">
                  <Inbox className="h-5 w-5" strokeWidth={1.5} />
                  No enquiries linked to this Lead yet.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(210,30%,97%)] text-left text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="px-6 py-3">Code</th>
                      <th className="px-6 py-3">Subject</th>
                      <th className="px-6 py-3">Source</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Received</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(enquiries.data?.data ?? []).map((e) => (
                      <tr
                        key={e.id}
                        className="cursor-pointer hover:bg-[hsl(210,50%,98%)]"
                        onClick={() => navigate(`/enquiries/${e.id}`)}
                      >
                        <td className="px-6 py-4 font-mono text-xs text-muted-foreground">
                          {e.code}
                        </td>
                        <td className="px-6 py-4 text-foreground">
                          {e.subject ?? "—"}
                        </td>
                        <td className="px-6 py-4 text-muted-foreground">
                          {e.source}
                        </td>
                        <td className="px-6 py-4 text-muted-foreground">
                          {e.status}
                        </td>
                        <td className="px-6 py-4 text-muted-foreground">
                          {formatDate(e.createdAt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        )}

        {tab === "notes" && (
          <Card style={{ borderColor: CARD_BORDER }}>
            <CardContent className="p-6 text-sm text-muted-foreground">
              Notes module integration coming soon — for quick lead-level
              notes use the <span className="font-medium">Notes</span> field in
              the Pipeline card above.
            </CardContent>
          </Card>
        )}

        {tab === "tasks" && (
          <Card style={{ borderColor: CARD_BORDER }}>
            <CardContent className="p-6 text-sm text-muted-foreground">
              Lead-scoped Tasks are wired through Convert-to-Contact's
              follow-up workflow. A dedicated Lead → Tasks tab will land in a
              follow-up commit; tasks already created against this Lead live in
              the standard <button
                className="text-primary hover:underline"
                onClick={() => navigate("/tasks")}
              >
                Tasks
              </button>{" "}
              module.
            </CardContent>
          </Card>
        )}

        {tab === "activity" && (
          <Card style={{ borderColor: CARD_BORDER }}>
            <CardContent className="p-6">
              {timeline.isLoading ? (
                <div className="flex items-center justify-center py-6 text-sm text-muted-foreground">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                  Loading timeline…
                </div>
              ) : (timeline.data ?? []).length === 0 ? (
                <div className="py-6 text-center text-sm text-muted-foreground">
                  No activity recorded yet.
                </div>
              ) : (
                <ol className="space-y-3">
                  {(timeline.data ?? []).map((e, i) => (
                    <li
                      key={`${e.at}-${i}`}
                      className="flex gap-3 border-l-2 border-border pl-3"
                    >
                      <div className="flex-1">
                        <div className="text-sm text-foreground">
                          {e.description}
                          {e.ref?.kind === "enquiry" && (
                            <button
                              className="ml-2 text-xs text-primary hover:underline"
                              onClick={() =>
                                navigate(`/enquiries/${e.ref!.id}`)
                              }
                            >
                              open →
                            </button>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(e.at).toLocaleString("en-GB")} · {e.type.replace(/_/g, " ")}
                        </div>
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        )}

        <FindDuplicatesModal
          lead={lead}
          open={findDupOpen}
          onOpenChange={setFindDupOpen}
        />

        <ConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete Lead?"
          description={`Delete ${lead.code}? Linked enquiries will keep their data but lose the Lead aggregate.`}
          onConfirm={async () => {
            await del.mutateAsync(lead.id);
            navigate("/leads");
          }}
        />
      </div>
    </AppLayout>
  );
}
